import SubSectionNav from "@components/Blocks/SubSectionNav/SubSectionNav";
import { SectionTitleV2 } from "@elements/Typography/TitleV2";
import { getGTMData, getMetaData } from "@helper/getMetaData";
import { Route } from "@route-types/Section/+types/Section";
import BHBasePage, { BHBasePageProps } from "@routes/BHBasePage";
import ErrorPage from "@routes/ErrorPage";
import WidgetWrapper from "@routes/Home/HomePageLayout/WidgetWrapper";
import NotFound from "@routes/NotFound";
import type {
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { ReactElement } from "react";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import fetchSectionData, { SectionPayloadResponseType } from "./Section.server";
import { SectionMetaData } from "./SectionMetaData";
import SectionArticlesListNew from "./SectionPageLayout/SectionArticleListNew";

export function meta({
  loaderData,
}: {
  loaderData: TRouteWithoutRedirect<SectionPayloadResponseType, string>;
}) {
  // console.log("Section meta loaderData:", loaderData);
  const payload = loaderData.payload;

  if (typeof payload !== "object" || !("data" in payload)) return [];

  const sectionArticles = payload.data;
  if (sectionArticles.length === 0) return [];

  const { section: sectionName, paywall } = payload;
  const { parentSection } = sectionArticles[0];

  const { metaTags } = SectionMetaData({ sectionName, parentSection, paywall });
  const metaRes = getMetaData(metaTags);

  return [metaRes];
}

export default function SectionPage(): ReactElement {
  const dataLoaderResponse: TRouteWithoutRedirect<
    SectionPayloadResponseType,
    string
  > = useLoaderData();

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  const {
    data: sectionArticles,
    paywall,
    section: sectionName,
  } = dataLoaderResponse.payload;
  const parentSection = sectionArticles[0].parentSection;

  const adsSectionName = !paywall
    ? sectionArticles[0].adsUniqueSectionName
    : sectionName;
  const sectionNameNavObj = !paywall
    ? sectionArticles[0].parentSection
      ? sectionArticles[0].uniqueParentSectionName
      : sectionArticles[0].uniqueSectionName
    : sectionName;

  const displaySectionName = !paywall
    ? parentSection
      ? parentSection
      : sectionArticles[0].sectionName
    : sectionName;
  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;
  const basePageProps: BHBasePageProps = {
    adsSectionName: adsSectionName,
    // gaData: gaData,
    // metaTags: metaTags,
    pageAdTargetValue: pageAdTargetValue,
    hideScrollableSectionMenu: true,
  };

  const renderListingPage = () => {
    return (
      <div className="m-auto">
        <WidgetWrapper>
          <SectionTitleV2 sectionName={displaySectionName} />
          <SubSectionNav
            sectionName={sectionNameNavObj || ""}
            displaySection={displaySectionName || ""}
          />
        </WidgetWrapper>
        <div className="flex flex-col lg:flex-row w-full gap-4">
          <SectionArticlesListNew
            sectionArticles={sectionArticles}
            sectionName={sectionName}
            adsSectionName={adsSectionName}
            paywall={paywall}
          />
        </div>
      </div>
    );
  };

  return <BHBasePage {...basePageProps}>{renderListingPage()}</BHBasePage>;
}

export async function loader({ request }: Route.LoaderArgs) {
  // Parse URL with localhost base to handle both relative paths ("/section") and absolute URLs ("https://localhost:5174/section")
  const url = new URL(request.url, `http://localhost`);
  // Extract path segments and remove empty strings (e.g., "/news/singapore" becomes ["news", "singapore"])
  const sectionLevels: string[] = url.pathname.split("/").filter(Boolean);
  const sectionName = sectionLevels[1] ? sectionLevels[1] : sectionLevels[0];
  const parentSection = sectionLevels[1] ? sectionLevels[0] : "";
  const data = await fetchSectionData(sectionLevels);
  const { gaData } = SectionMetaData({
    sectionName,
    parentSection,
  });

  return {
    ...data,
    gtm: getGTMData(gaData),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<SectionPayloadResponseType | null, string>
  >;
}
